/* This component should substitute MultiDateAutoFillPicker once DEV-95888 is done */
import React, { Suspense, useCallback, useEffect, useId, useMemo } from 'react';
import DayPicker, { DateUtils } from 'react-day-picker';
import MomentLocaleUtils from 'react-day-picker/moment';
import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import moment from 'moment';

import ScrollableDayPicker from '@rover/components/src/datetime/ScrollableDayPicker/ScrollableDayPicker';
import FormBasicValidationError from '@rover/components/src/formFields/FormValidationError/FormBasicValidationError';
import InlineErrorDateInput from '@rover/components/src/formFields/InlineErrorDateInput/InlineErrorDateInputGeneric';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import Calendar from '@rover/icons/svg/calendar.svg';
import { Box, Button, Flex, Heading, Paragraph } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet';
import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';
import useTheme from '@rover/kibble/themes/useTheme';
import LabelAndErrorFormGroup from '@rover/react-lib/src/components/utils/LabelAndErrorFormGroup';
import dayFactory from '@rover/react-lib/src/factories/dayFactory';
import useOnClickOutside from '@rover/react-lib/src/hooks/useOnClickOutside';
import { useSchedulerForm } from '@rover/react-lib/src/pages/contact-sitter/ContactSitterPage/components/ServiceScheduler/contexts/SchedulerFormContext';
import type { Day } from '@rover/types/src/datetime/Day';
import getDateTimeFormatMapForLocale from '@rover/utilities/datetime';

import {
  createRenderDayForHolidays,
  DatePickerCaption,
  DatePickerNavbar,
  DatePickerStylingWrapper,
} from '../DatePicker';
import {
  MultiDateAutoFillPickerProps,
  numberOfMonthsAllowed,
  StyledCloseIcon,
} from '../GenericCalendar';

const DayPickerLazy = React.lazy(() => import('react-day-picker'));

const PARTIAL_DATE_FORMAT = 'ddd, DD MMM';
const COMPLETE_DATE_FORMAT = 'ddd, DD MMM YYYY';

const SELECT_END_DATE = msg`Select end date`;

type DayPickerClickModifiers = {
  disabled: boolean;
  selected: boolean;
};

type KeyboardEvent = {
  key: string;
  preventDefault(): void;
};

export function getMiddleDays<T extends Day>(
  firstDay: T,
  lastDay: T,
  DF: Record<string, string>
): T[] {
  const difference =
    Math.abs(
      moment(firstDay.date).startOf('day').diff(moment(lastDay.date).startOf('day'), 'days')
    ) - 1;
  const allDays: T[] = [firstDay];
  let currentDay = moment(firstDay.date).add(1, 'days');

  while (allDays.length < difference + 1) {
    allDays.push(
      // @ts-expect-error TS is mad we're assigning a Day to T, when T could have extra props.  This isn't great but the consumers of the component seem to handle it fine
      dayFactory({
        name: moment(currentDay).format(DF.MONTH_DAY_MED),
        date: currentDay.toDate(),
      })
    );
    currentDay = moment(currentDay).add(1, 'days');
  }
  allDays.push(
    // @ts-expect-error TS is mad we're assigning a Day to T, when T could have extra props.  This isn't great but the consumers of the component seem to handle it fine
    dayFactory({
      name: moment(lastDay.date).format(DF.MONTH_DAY_MED),
      date: lastDay.date,
    })
  );
  return allDays;
}

function getFirstDay<T extends Day>(selectedDays: T[], isMobile = false): string {
  const firstDay = selectedDays[0].date;
  if (selectedDays.length > 1) {
    const lastDay = selectedDays[selectedDays.length - 1].date;
    // If the first and last day are in the same year, or it's displayed on the bottom sheet, we don't show the year.
    if (firstDay?.getFullYear() === lastDay?.getFullYear() || isMobile) {
      return moment(firstDay).format(PARTIAL_DATE_FORMAT);
    }
    return moment(firstDay).format(COMPLETE_DATE_FORMAT);
  }
  return moment(firstDay).format(PARTIAL_DATE_FORMAT);
}

export function getStringFromDates<T extends Day>(selectedDays: T[]): string {
  if (selectedDays.length === 0) {
    return '';
  }
  const firstDay = getFirstDay(selectedDays);
  if (selectedDays.length === 1) {
    return `${firstDay} -`;
  }
  const lastDay = moment(selectedDays[selectedDays.length - 1].date);
  return `${firstDay} - ${lastDay.format(COMPLETE_DATE_FORMAT)}`;
}

export function clearDaysSelection<T extends Day>(
  selectedDays: T[],
  onChange: (days: Array<Day & T>) => void,
  all = false
): void {
  selectedDays.splice(all ? 0 : 1, selectedDays.length);
  onChange([...selectedDays]);
}

function handleDayClick<T extends Day>(
  selectedDays: T[],
  onChange: (days: Array<Day & T>) => void,
  clickedDate: Date,
  // eslint-disable-next-line default-param-last
  { selected, disabled }: DayPickerClickModifiers = { selected: false, disabled: false },
  locale: Locale
): void {
  if (disabled) return;
  if (selected) {
    if (selectedDays.length === 1) {
      const DF = getDateTimeFormatMapForLocale(locale);
      selectedDays.push(
        // @ts-expect-error TS is mad we're assigning a Day to T, when T could have extra props.  This isn't great but the consumers of the component seem to handle it fine
        dayFactory({
          name: moment(clickedDate).format(DF.MONTH_DAY_MED),
          date: clickedDate,
        })
      );
      getStringFromDates(selectedDays);
      onChange([...selectedDays]);
      return;
    }
    if (selectedDays.length === 2) {
      const areSameDay = DateUtils.isSameDay(
        selectedDays[0].date as Date,
        selectedDays[1].date as Date
      );
      if (areSameDay) {
        clearDaysSelection(selectedDays, onChange, true);
      }
    }
    const selectedDates = selectedDays.map((d) => d.date);
    const selectedIndex = selectedDates.findIndex((d) => d && DateUtils.isSameDay(d, clickedDate));
    if (selectedDates.length - 1 === selectedIndex) {
      selectedDays.splice(selectedIndex, selectedDates.length - selectedIndex);
    } else {
      selectedDays.splice(selectedIndex + 1, selectedDates.length - selectedIndex);
    }
  } else {
    const DF = getDateTimeFormatMapForLocale(locale);
    if (selectedDays.length >= 1) {
      if (moment(clickedDate).isBefore(moment(selectedDays[0].date))) {
        while (selectedDays.length > 0) {
          selectedDays.pop();
        }
        selectedDays.push(
          // @ts-expect-error TS is mad we're assigning a Day to T, when T could have extra props.  This isn't great but the consumers of the component seem to handle it fine
          dayFactory({
            name: moment(clickedDate).format(DF.MONTH_DAY_MED),
            date: clickedDate,
          })
        );
        onChange([...selectedDays]);
        return;
      }
      selectedDays.push(
        // @ts-expect-error TS is mad we're assigning a Day to T, when T could have extra props.  This isn't great but the consumers of the component seem to handle it fine
        dayFactory({
          name: moment(clickedDate).format(DF.MONTH_DAY_MED),
          date: clickedDate,
        })
      );
      selectedDays.sort((a, b) => moment(a.date).diff(moment(b.date)));
      const firstDay = selectedDays[0];
      const lastDay = selectedDays[selectedDays.length - 1];
      while (selectedDays.length > 0) {
        selectedDays.pop();
      }
      const newDays = getMiddleDays(firstDay, lastDay, DF);
      newDays.forEach((day) => {
        selectedDays.push(day);
      });
    } else {
      selectedDays.push(
        // @ts-expect-error TS is mad we're assigning a Day to T, when T could have extra props.  This isn't great but the consumers of the component seem to handle it fine
        dayFactory({
          name: moment(clickedDate).format(DF.MONTH_DAY_MED),
          date: clickedDate,
        })
      );
    }
  }
  getStringFromDates(selectedDays);
  onChange([...selectedDays]);
}

function checkStartDateError(i18n: I18n, selectedDays): undefined | string {
  if (selectedDays.length > 0) {
    return undefined;
  }
  return i18n._(msg`Select start date`);
}

function checkEndDateError(i18n: I18n, selectedDays): undefined | string {
  if (selectedDays.length <= 1) {
    return i18n._(SELECT_END_DATE);
  }
  return undefined;
}

function checkDateErrors(i18n: I18n, selectedDays, disableValidation = false): undefined | string {
  if (disableValidation) return undefined;

  if (!selectedDays.length) {
    return i18n._(msg`Select start and end dates`);
  }
  if (selectedDays.length === 1) {
    return i18n._(SELECT_END_DATE);
  }
  return undefined;
}

function MultiDateAutoFillPicker({
  className = '',
  disableAfterDateTime,
  disableBeforeDateTime = new Date(),
  holidays = [],
  initialMonth,
  locale,
  onChange,
  disabledDays = [],
  isDayBlocked,
  selectedDays,
  isMobileBrowser,
  serviceName,
  maxDate,
  newCalendarInteraction = false,
  showTwoInputs = false,
  endDatePlaceholder,
  startDatePlaceholder,
  disableValidation = false,
  validationType,
  inputStyle,
  onClickFromSearch,
  isDismissible,
  ...other
}: MultiDateAutoFillPickerProps<Day>): JSX.Element {
  const { t, i18n } = useLingui();
  const theme = useTheme();
  const dayPickerProps = {
    fromMonth: disableBeforeDateTime,
    toMonth: disableAfterDateTime,
    initialMonth,
    disabledDays: [
      { before: disableBeforeDateTime, after: disableAfterDateTime },
      isDayBlocked,
      ...disabledDays,
    ],
    selectedDays: selectedDays.map((d) => d.date),
    maxDate,
    onDayClick: (clickedDate, modifiers) =>
      handleDayClick<Day>(selectedDays, onChange, clickedDate, modifiers, locale),
    navbarElement: DatePickerNavbar,
    captionElement: DatePickerCaption,
    localeUtils: MomentLocaleUtils,
    locale,
    modifiers: {
      holiday: holidays,
    },
    renderDay: createRenderDayForHolidays(holidays),
  };
  const calendarWrapperId = useId();
  const calendarWrapperRef = React.useRef<HTMLDivElement>(null);
  const [showCalendar, setShowCalendar] = React.useState<boolean | null>(null);
  const [localError, setLocalError] = React.useState<undefined | string>(undefined);
  const [localBottomSheetError, setLocalBottonSheetError] = React.useState<undefined | string>(
    undefined
  );
  const DF = useMemo(() => getDateTimeFormatMapForLocale(locale), [locale]);
  const { isDirty, onFormDirtinessChanged } = useSchedulerForm();

  useEffect(() => {
    if (selectedDays.length === 0) {
      setLocalError(undefined);
      setLocalBottonSheetError(undefined);
    }
  }, [serviceName]);

  useEffect(() => {
    const rangeDays = moment(selectedDays[0]?.date).diff(
      selectedDays[selectedDays.length - 1]?.date,
      'days'
    );
    if (rangeDays && rangeDays + 1 !== selectedDays.length) {
      const newDays = getMiddleDays(selectedDays[0], selectedDays[selectedDays.length - 1], DF);

      clearDaysSelection(selectedDays, onChange, true);
      newDays.forEach((day) => {
        selectedDays.push(day);
      });
      getStringFromDates(selectedDays);
      onChange([...selectedDays]);
    }
  }, []);

  useEffect(() => {
    if (showCalendar && onClickFromSearch) onClickFromSearch();
    if (showCalendar === false)
      setLocalError(checkDateErrors(i18n, selectedDays, disableValidation));
  }, [showCalendar, disableValidation, onClickFromSearch]);

  useOnClickOutside(calendarWrapperRef, () => {
    if (showCalendar) {
      setShowCalendar(false);
      setLocalError(checkDateErrors(i18n, selectedDays, disableValidation));
    }
  });

  useEffect(() => {
    if (!isDirty) {
      setLocalError(undefined);
    }
  }, [isDirty]);

  useEffect(() => {
    if (localError) {
      onFormDirtinessChanged?.(true);
    }
  }, [localError, onFormDirtinessChanged]);

  const handleCalendarToggle = useCallback(
    () => setShowCalendar((prevShowCalendar) => !prevShowCalendar),
    []
  );

  const handleClearDates = useCallback(() => {
    if (selectedDays.length) {
      setLocalBottonSheetError(undefined);
    }
    clearDaysSelection(selectedDays, onChange, true);
  }, [onChange, selectedDays]);

  const renderInput = (validationError): JSX.Element => {
    return (
      <InlineErrorDateInput
        aria-expanded={showCalendar}
        aria-controls={showCalendar && calendarWrapperId}
        aria-labelledby={other['aria-labelledby']}
        aria-label={other['aria-label']}
        onKeyDown={(event: KeyboardEvent): void => {
          if (event.key === 'Enter') {
            setShowCalendar(!showCalendar);
          } else if (event.key === ' ') {
            event.preventDefault();
            setShowCalendar(!showCalendar);
          }
        }}
        value={getStringFromDates(selectedDays)}
        errorMessage={
          validationError ? checkDateErrors(i18n, selectedDays, disableValidation) : undefined
        }
        onClick={(): void => {
          setShowCalendar(!showCalendar);
        }}
        onIconClick={(): void => {
          // if it shows X button, we clear selection, otherwise toggle the calendar
          if (selectedDays.length >= 1) {
            clearDaysSelection(selectedDays, onChange, true);
            setLocalError(checkDateErrors(i18n, selectedDays, disableValidation));
          } else {
            setShowCalendar(!showCalendar);
          }
        }}
        icon={selectedDays.length >= 1 && <StyledCloseIcon />}
      />
    );
  };

  const renderTwoInputs = (validationError?: string | null): JSX.Element => {
    const calendarIcon = <Calendar width="24px" height="24px" fill={theme.colors.text.tertiary} />;

    return (
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Box flexDirection="column" alignItems="flex-start" flex="1" position="relative">
          <label htmlFor="startDateInput" hidden>
            <Trans>Start date</Trans>
          </label>
          <InlineErrorDateInput
            style={inputStyle}
            className={className}
            inputId="startDateInput"
            value={selectedDays.length > 0 ? getFirstDay(selectedDays, true) : ''}
            errorMessage={
              validationError || localError ? checkStartDateError(i18n, selectedDays) : undefined
            }
            placeholder={startDatePlaceholder}
            onClick={handleCalendarToggle}
            onIconClick={handleCalendarToggle}
            icon={calendarIcon}
            aria-controls={showCalendar ? calendarWrapperId : undefined}
            aria-expanded={showCalendar}
          />
        </Box>
        <Box
          flexDirection="column"
          alignItems="flex-start"
          marginLeft="4x"
          position="relative"
          flex="1"
        >
          <label htmlFor="endDateInput" hidden>
            <Trans>End date</Trans>
          </label>
          <InlineErrorDateInput
            style={inputStyle}
            inputId="endDateInput"
            value={
              selectedDays.length > 1
                ? moment(selectedDays[selectedDays.length - 1].date).format(PARTIAL_DATE_FORMAT)
                : ''
            }
            errorMessage={
              validationError || localError ? checkEndDateError(i18n, selectedDays) : undefined
            }
            placeholder={endDatePlaceholder}
            onClick={handleCalendarToggle}
            onIconClick={handleCalendarToggle}
            icon={calendarIcon}
            aria-controls={showCalendar ? calendarWrapperId : undefined}
            aria-expanded={showCalendar}
          />
        </Box>
      </Box>
    );
  };

  const ariaAnnouncement = (): string => {
    if (selectedDays.length === 0) {
      return t`Select start and end date`;
    }
    if (selectedDays.length === 1) {
      return t`Start date ${moment(selectedDays[0].date).format(DF.DATE_FULL)}. Select end date.`;
    }
    return t`Selected dates from ${moment(selectedDays[0].date).format(DF.DATE_FULL)} to ${moment(
      selectedDays[selectedDays.length - 1].date
    ).format(DF.DATE_FULL)}. Tap "Save Dates" to close calendar.`;
  };

  const dynamicSubtitle = (): string | undefined => {
    if (selectedDays.length === 0) {
      return t`Select start and end dates`;
    }
    if (selectedDays.length === 1) {
      return t`Select end date`;
    }
    return undefined;
  };

  const renderMobile = (formGroupProps): JSX.Element => {
    return (
      <BottomSheet
        isOpen={!!showCalendar}
        onRequestClose={() => setShowCalendar(false)}
        isDismissible={isDismissible}
      >
        <DatePickerStylingWrapper
          id={calendarWrapperId}
          className={className}
          styleAsError={!!(other.validationError || localError)}
          newCalendarInteraction={newCalendarInteraction}
          isMobileBrowser={isMobileBrowser}
          ref={isMobileBrowser ? null : calendarWrapperRef}
        >
          <Flex flexDirection="column" alignItems="center">
            <A11yHiddenBox aria-live="assertive" role="alert">
              {ariaAnnouncement()}
            </A11yHiddenBox>
            <Heading size="200" mb="1x">
              {serviceName as string}
            </Heading>
            <Paragraph
              size="100"
              mb="6x"
              textAlign="center"
              webProps={{ maxWidth: '80vw' }}
              textColor="secondary"
            >
              {dynamicSubtitle()}
            </Paragraph>
            <ScrollableDayPicker
              wrapperStyles={{ overflow: 'auto', height: '350px', width: '100%' }}
              initialMonth={dayPickerProps.initialMonth}
            >
              <Suspense fallback={null}>
                <DayPickerLazy
                  {...formGroupProps}
                  {...dayPickerProps}
                  initialMonth={undefined}
                  numberOfMonths={numberOfMonthsAllowed(maxDate)}
                  canChangeMonth={false}
                />
              </Suspense>
            </ScrollableDayPicker>
            {localBottomSheetError && (
              <Flex marginLeft="0x" width="100%">
                <FormBasicValidationError
                  errorMessage={checkDateErrors(i18n, selectedDays) as string}
                  id="input-error"
                />
              </Flex>
            )}

            <Button
              mt="6x"
              size="small"
              variant="primary"
              fullWidth
              sx={{
                height: '8x',
              }}
              padding="0x"
              onClick={(): void => {
                const error = checkDateErrors(i18n, selectedDays);
                if (!disableValidation) setLocalError(error);
                setLocalBottonSheetError(error);
                if (!error) {
                  setShowCalendar(false);
                }
              }}
            >
              {t`Save dates`}
            </Button>
            <Button
              mt="3x"
              size="small"
              variant="flat"
              height="8x"
              fullWidth
              padding="0x"
              onClick={handleClearDates}
            >
              {t`Clear dates`}
            </Button>
          </Flex>
        </DatePickerStylingWrapper>
      </BottomSheet>
    );
  };

  const renderDesktop = (formGroupProps): JSX.Element => {
    return (
      <Box
        background="primary"
        id={calendarWrapperId}
        borderWidth="primary"
        borderStyle="solid"
        borderColor="primary"
        borderRadius="secondary"
        pb="0x"
        mt="5x"
        width="640px"
      >
        <DayPicker {...formGroupProps} {...dayPickerProps} numberOfMonths={2} />
        <Flex flexDirection="row" justifyContent="space-between" margin="6x" marginTop="3x">
          <Button
            onClick={handleClearDates}
            webProps={{
              type: 'button',
              'data-qa-id': 'clear-selection-button',
              'aria-label': 'Clear selection',
            }}
            width={180}
            paddingY="2x"
            size="small"
          >
            {t`Reset dates`}
          </Button>
          <Button
            onClick={() => setShowCalendar(false)}
            width={180}
            variant="primary"
            paddingY="2x"
            webProps={{
              type: 'button',
              'data-qa-id': 'save-selection-button',
              'aria-label': 'Save dates',
            }}
            data-testid="save-selection-button"
            size="small"
          >
            {t`Save dates`}
          </Button>
        </Flex>
      </Box>
    );
  };
  return (
    <LabelAndErrorFormGroup
      {...other}
      useLabelElementAsCaption={false}
      validationError={newCalendarInteraction ? undefined : other.validationError} // phasing out popovers in favour of inline errors
      validationType={validationType}
    >
      {(formGroupProps): JSX.Element => (
        <DatePickerStylingWrapper
          data-testid="multi-date-auto-fill-picker"
          className={className}
          styleAsError={!!(other.validationError || localError)}
          newCalendarInteraction={newCalendarInteraction}
          isMobileBrowser={isMobileBrowser}
          ref={isMobileBrowser ? null : calendarWrapperRef}
        >
          {!showTwoInputs && renderInput(other.validationError || localError)}
          {showTwoInputs && renderTwoInputs(other.validationError)}
          {showCalendar && isMobileBrowser && renderMobile(formGroupProps)}
          {showCalendar && !isMobileBrowser && renderDesktop(formGroupProps)}
        </DatePickerStylingWrapper>
      )}
    </LabelAndErrorFormGroup>
  );
}

export default MultiDateAutoFillPicker;
